import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, AlertTriangle, Clock, Package, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";

const typeConfig = {
  expired: { icon: AlertCircle, color: "text-[#C65D47]", bg: "bg-[#FCEAE8]" },
  expiring: { icon: Clock, color: "text-[#8B6518]", bg: "bg-[#FDF6E3]" },
  stagnant: { icon: Package, color: "text-[#2F5A78]", bg: "bg-[#EDF5F9]" },
  low_stock: { icon: AlertTriangle, color: "text-[#8B6518]", bg: "bg-[#FDF6E3]" },
};

export default function NotificationsPopover({ alertCount, onCount }) {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.getAlerts().then((d) => {
        setAlerts(d);
        onCount?.(d.length);
        setLoading(false);
      });
    }
  }, [open, onCount]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="notifications-btn"
          variant="ghost"
          size="icon"
          className="relative hover:bg-[#F4F1ED] h-10 w-10"
        >
          <Bell size={18} />
          {alertCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-[#C65D47] text-white text-[10px] font-semibold flex items-center justify-center"
            >
              {alertCount > 99 ? "99+" : alertCount}
            </motion.span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 border-[#EAE7E1]">
        <div className="p-4 border-b border-[#EAE7E1]">
          <div className="font-semibold text-sm" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Avvisi magazzino
          </div>
          <div className="text-xs text-[#8E918C] mt-0.5">
            {alerts.length > 0 ? `${alerts.length} elementi richiedono attenzione` : "Tutto sotto controllo"}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-[#8E918C]">Caricamento...</div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8E918C]">
              Nessun avviso al momento.
            </div>
          ) : (
            alerts.slice(0, 20).map((a) => {
              const cfg = typeConfig[a.type] || typeConfig.expiring;
              const Icon = cfg.icon;
              return (
                <div
                  key={a.id}
                  data-testid={`alert-item-${a.type}`}
                  className="flex items-start gap-3 p-4 border-b border-[#EAE7E1] last:border-0 hover:bg-[#F9F8F6] transition-colors"
                >
                  <div className={`h-9 w-9 rounded-lg ${cfg.bg} ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.product_name}</div>
                    <div className="text-xs text-[#5C5F5A] mt-0.5">{a.message}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
